import { UserDepartment, type User } from "@/api/user/models";
import { RouteEntry } from "@/interfaces/route";
import { RoutePathEnum } from "@/routes/paths";
import { DEPARTMENT_TO_SLUG, DepartmentSlug, isDepartmentSlugValid, SLUG_TO_DEPARTMENT } from "@/types/department";
import { getFirstUserDepartment } from "@/utils/user-positions";

/**
 * Converte UserDepartment para slug de URL (kebab-case).
 *
 * @param dept - Department do usuário
 * @returns Slug do department para uso em URLs
 *
 * @example
 * getDepartmentSlug(UserDepartment.CASTING) // returns "casting"
 */
export function getDepartmentSlug(dept: UserDepartment): DepartmentSlug {
  return DEPARTMENT_TO_SLUG[dept];
}

/**
 * Constrói uma rota completa com prefixo de department.
 *
 * @param dept - UserDepartment ou DepartmentSlug
 * @param path - Caminho da rota (ex: '/talentos' ou 'talentos')
 * @returns Rota completa com prefixo de department
 *
 * @example
 * buildDepartmentRoute('CASTING', '/talentos') // returns "/casting/talentos"
 * buildDepartmentRoute('casting', 'talentos') // returns "/casting/talentos"
 */
export function buildDepartmentRoute(dept: UserDepartment | DepartmentSlug, path: string): string {
  // Converte UserDepartment para slug se necessário
  const slug =
    typeof dept === "string" && dept.toLowerCase() === dept ? dept : getDepartmentSlug(dept as UserDepartment);

  // Normaliza path (remove / inicial se existir)
  const normalizedPath = path.startsWith("/") ? path.slice(1) : path;

  return `/${slug}/${normalizedPath}`;
}

export function getDepartmentHome(department: UserDepartment | DepartmentSlug): string {
  const normalizedDepartment =
    typeof department === "string" && department.toLowerCase() === department
      ? SLUG_TO_DEPARTMENT[department as DepartmentSlug]
      : (department as UserDepartment);

  if (normalizedDepartment === UserDepartment.REBOUCAS || normalizedDepartment === UserDepartment.FRADIQUE) {
    return RoutePathEnum.SCHEDULE;
  }

  return RoutePathEnum.TALENTS;
}

/**
 * Adiciona prefixo [department] explícito nas definições de flat routes.
 * Usado para envolver todas as rotas protegidas com o segmento de department.
 *
 * @param routes - Array de RouteEntry a ser envolvido
 * @returns RouteEntry com path "[department]" e children contendo as rotas fornecidas
 *
 * @example
 * const routesEntries: RouteEntry[] = withDepartmentPrefix([
 *   { path: "talentos", name: "Talentos" },
 *   { path: "jobs", name: "Jobs" }
 * ]);
 * // Resulta em: { path: "[department]", children: [...] }
 */
export function withDepartmentPrefix(routes: RouteEntry[]): RouteEntry {
  return {
    name: "Department",
    path: "[department]",
    children: routes,
  };
}

// Fallback routes use the first position; preserving a URL still requires that position below.
export function getUserInitialDepartmentRoute(user: Pick<User, "positions">): string | null {
  const firstDepartment = getFirstUserDepartment(user);

  if (!firstDepartment) {
    return null;
  }

  return buildDepartmentRoute(firstDepartment, getDepartmentHome(firstDepartment));
}

/**
 * Extrai o slug do departamento do pathname.
 *
 * @param pathname - Pathname atual da rota
 * @returns Slug do departamento extraído do pathname ou null se não for válido
 */
export function getDepartmentSlugFromPathname(pathname = window.location.pathname): DepartmentSlug | null {
  const slugFromPathname = pathname.split("/").find(Boolean);

  if (!slugFromPathname || !isDepartmentSlugValid(slugFromPathname)) {
    return null;
  }

  return slugFromPathname;
}

/**
 * Obtém o UserDepartment correspondente ao slug extraído do pathname.
 *
 * @param pathname - Pathname atual da rota
 * @returns UserDepartment correspondente ao slug ou null se não for válido
 */
export function getDepartmentFromPathname(pathname = window.location.pathname): UserDepartment | null {
  const slugFromPathname = getDepartmentSlugFromPathname(pathname);

  if (!slugFromPathname) {
    return null;
  }

  return SLUG_TO_DEPARTMENT[slugFromPathname];
}
